import React, { useState, useEffect, useRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import classes from './Checklist.module.css';
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../config';
import { generateId, matchCatalogItem, getItemCategories, getDefaultQuantitiesForScreen } from '../../config/helpers';
import { UserContext } from '../../store/user-context';

const XIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6L18 18" stroke="#565959" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const NEWLINE_RE = /\r\n|\n|\r/;
// Markdown task-list prefix: "- [x] ", "- [ ] ", "* [X] " etc.
const TASK_LIST_RE = /^[-*]\s*\[[xX ]\]\s*/;

const InlineAdd = ({ activeCategory, group, onClose, onShowToast, screen }) => {
  const [rows, setRows] = useState(['']);
  const [pasteModal, setPasteModal] = useState(null); // { lines, duplicates } | null
  const inputRefs = useRef([]);
  const user = auth.currentUser;
  const UserCtx = useContext(UserContext);

  useEffect(() => {
    const last = inputRefs.current[rows.length - 1];
    if (last) last.focus();
  }, [rows.length]);

  const save = async () => {
    const validItems = rows.map(r => r.trim()).filter(Boolean);
    onClose();
    if (validItems.length === 0) return;

    const cat = activeCategory || group.categories[0];
    const existing = group.items || [];
    const existingNames = existing.map(i => i.name.toLowerCase());
    const duplicates = validItems.filter(n => existingNames.includes(n.toLowerCase()));
    const newOnes = validItems.filter(n => !existingNames.includes(n.toLowerCase()));

    let updatedItems = existing.map(item => {
      if (duplicates.map(d => d.toLowerCase()).includes(item.name.toLowerCase())) {
        const cats = getItemCategories(item);
        if (!cats.includes(cat)) return { ...item, categories: [...cats, cat] };
      }
      return item;
    });

    // Order new items below the current lowest order so they always sort to
    // the top, even across repeated adds (see AddSheet.js's addGearItems).
    const minOrder = Math.min(0, ...existing.map(item => item.order ?? 0));
    // Carry hire/tip/aka through from the catalog when the typed name
    // matches a known item — same source Root.js's populateItems already
    // spreads at signup time.
    const festival = UserCtx?.festival || group?.festival;
    const defaultItems = UserCtx?.itemInfo;
    const itemInfoList = festival?.items || defaultItems || [];
    const newItems = newOnes.map((name, i) => {
      const match = matchCatalogItem(name, itemInfoList);
      return {
        id: generateId(20),
        name,
        categories: [cat],
        order: minOrder - newOnes.length + i,
        addedManually: true,
        organise: [{ assigned: user?.uid || 'unassigned', method: null, checked: false }],
        ...getDefaultQuantitiesForScreen(screen),
        tip: match?.tip || null,
        hire: match?.hire || false,
        aka: match?.aka || null,
      };
    });

    // Prepend (not append) — Need/Pack render items in array order and
    // don't consult `order`, so physical position is what puts them on top there.
    updatedItems = [...newItems, ...updatedItems];
    const groupRef = doc(db, 'groups', group.id);
    await updateDoc(groupRef, { items: updatedItems }).catch(console.error);
  };

  // Every pasted item lands in whichever category tab was active when the
  // paste was initiated (or the group's first category, on the All tab) —
  // not a per-item catalog-matched category — so the confirmation step can
  // name a single target category, and so staying on that tab afterwards
  // (no redirect) actually shows everything that was just added.
  const targetCategory = activeCategory || group.categories[0];

  const addItems = async (names) => {
    onClose();
    if (names.length === 0) return;
    const existing = group.items || [];
    const minOrder = Math.min(0, ...existing.map(item => item.order ?? 0));
    const festival = UserCtx?.festival || group?.festival;
    const defaultItems = UserCtx?.itemInfo;
    const itemInfoList = festival?.items || defaultItems || [];

    const newItems = names.map((name, i) => {
      const match = matchCatalogItem(name, itemInfoList);
      return {
        id: generateId(20),
        name,
        categories: [targetCategory],
        order: minOrder - names.length + i,
        addedManually: true,
        organise: [{ assigned: user?.uid || 'unassigned', method: null, checked: false }],
        ...getDefaultQuantitiesForScreen(screen),
        tip: match?.tip || null,
        hire: match?.hire || false,
        aka: match?.aka || null,
      };
    });

    const updatedItems = [...newItems, ...existing];
    const groupRef = doc(db, 'groups', group.id);
    await updateDoc(groupRef, { items: updatedItems }).catch(console.error);

    // Deliberately no setActiveCategory call — stay on whichever tab was
    // active when the paste happened, since that's the category these
    // items were just added to.
    onShowToast && onShowToast(
      `${newItems.length} ${newItems.length === 1 ? 'item' : 'items'} added to ${targetCategory}`
    );
  };

  // Detects a multi-line paste and routes it into the bulk-add pipeline
  // instead of dumping raw text into a single-line input. A single-line
  // paste (or a multi-line paste that trims down to one usable name) falls
  // through untouched to the browser's normal paste behaviour.
  const handlePaste = (e) => {
    const text = e.clipboardData?.getData('text');
    if (!text || !NEWLINE_RE.test(text)) return;

    const lines = text.split(NEWLINE_RE)
      .map(l => l.trim().replace(TASK_LIST_RE, '').trim())
      .filter(Boolean);
    // De-duplicate within the pasted batch itself so "Tent\nTent" doesn't
    // create two identical new items.
    const uniqueLines = lines.filter((name, i) =>
      lines.findIndex(n => n.toLowerCase() === name.toLowerCase()) === i
    );
    if (uniqueLines.length <= 1) return;

    e.preventDefault();
    const existingNames = (group.items || []).map(i => i.name.toLowerCase());
    const duplicates = uniqueLines.filter(n => existingNames.includes(n.toLowerCase()));

    // Always confirm before adding anything — no more skipping straight to
    // addItems() just because there happen to be no duplicates.
    setPasteModal({ lines: uniqueLines, duplicates });
  };

  const resolvePasteModal = (choice) => {
    const { lines, duplicates } = pasteModal;
    setPasteModal(null);
    if (choice === 'cancel') return;
    // Confirming always skips duplicates — they're shown struck-through in
    // the list precisely so the user knows that's what "Add" will do.
    const dupSet = new Set(duplicates.map(d => d.toLowerCase()));
    addItems(lines.filter(n => !dupSet.has(n.toLowerCase())));
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (rows[index].trim() === '') {
        save();
      } else {
        setRows(prev => [...prev, '']);
      }
    }
    if (e.key === 'Escape') onClose();
  };

  return (
    <div className={classes.inlineAddCard}>
      {rows.map((row, index) => (
        <div
          key={index}
          className={`${classes.inlineRow} ${index > 0 ? classes.inlineRowBorder : ''}`}
        >
          <input
            ref={el => { inputRefs.current[index] = el; }}
            className={classes.inlineInput}
            type="text"
            placeholder={index === 0 ? 'Item name...' : 'Add another...'}
            value={row}
            onChange={e => {
              const updated = [...rows];
              updated[index] = e.target.value;
              setRows(updated);
            }}
            onKeyDown={e => handleKeyDown(e, index)}
            onPaste={handlePaste}
            spellCheck="false"
          />
          {index === 0 && (
            <>
              <button className={classes.inlineDoneBtn} onClick={save}>Done</button>
              <button className={classes.inlineCancelBtn} onClick={onClose} aria-label="Cancel">
                <XIcon />
              </button>
            </>
          )}
        </div>
      ))}

      {pasteModal && (() => {
        const dupSet = new Set(pasteModal.duplicates.map(d => d.toLowerCase()));
        const newCount = pasteModal.lines.length - pasteModal.duplicates.length;
        return ReactDOM.createPortal(
          <div className={classes.pasteModalOverlay}>
            <div className={classes.pasteModalBackdrop} onClick={() => resolvePasteModal('cancel')} />
            <div className={classes.pasteModalCard}>
              <h3 className={classes.pasteModalHeading}>
                Adding {newCount} {newCount === 1 ? 'item' : 'items'} to {targetCategory}
              </h3>
              <ul className={classes.pasteModalList}>
                {pasteModal.lines.map(name => {
                  const isDuplicate = dupSet.has(name.toLowerCase());
                  return (
                    <li key={name} className={isDuplicate ? classes.pasteModalItemDuplicate : undefined}>
                      <span className={isDuplicate ? classes.pasteModalItemNameStrike : undefined}>{name}</span>
                      {isDuplicate && <span className={classes.pasteModalItemDupLabel}>already exists</span>}
                    </li>
                  );
                })}
              </ul>
              <div className={classes.pasteModalActions}>
                <button className={classes.pasteModalBtnPrimary} onClick={() => resolvePasteModal('confirm')}>
                  Add {newCount} {newCount === 1 ? 'item' : 'items'}
                </button>
                <button className={classes.pasteModalBtnCancel} onClick={() => resolvePasteModal('cancel')}>
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

export default InlineAdd;
